import { X, CheckCircle2, XCircle } from 'lucide-react'
import { sonnerToast as toast } from '@workspace/ui/components/Sonner'
import { cn } from '@workspace/ui/lib/utils'
import { UserAvatar } from '@workspace/ui/components/UserAvatar'
import { Button } from '@workspace/ui/components/Button'
import type { Notification } from '@workspace/lib/api/notification'
import { getToastIconConfig } from '@/shared/utils/notification.utils'
import { NotificationContent } from './NotificationContent'

interface NotificationToastProps {
    id: string | number
    notification: Notification
}

export function NotificationToast({ id, notification }: NotificationToastProps) {
    const handleClose = () => {
        toast.dismiss(id)
    }

    const iconConfig = getToastIconConfig(notification.type)
    const actor = notification?.requester || notification.metadata?.createdBy
    const renderIcon = () => {
        if (iconConfig.type === 'avatar' && actor) {
            return (
                <UserAvatar
                    src={actor.avatar}
                    alt={actor.fullName}
                    fallback={actor.fullName}
                    size="md"
                    className="shrink-0"
                />
            )
        }

        const iconClasses = cn('size-8 shrink-0 rounded-lg flex items-center justify-center', iconConfig.bgClass)

        switch (iconConfig.icon) {
            case 'task':
                return (
                    <div className={iconClasses}>
                        <img src="/check-list.svg" className={cn('size-5', iconConfig.iconClass)} />
                    </div>
                )
            case 'check':
                return (
                    <div className={iconClasses}>
                        <CheckCircle2 className={cn('size-5', iconConfig.iconClass)} />
                    </div>
                )
            case 'x':
                return (
                    <div className={iconClasses}>
                        <XCircle className={cn('size-5', iconConfig.iconClass)} />
                    </div>
                )
            default:
                return null
        }
    }

    const displayTitle =
        (notification.type === 'new_request_approval' || notification.type === 'task_comment') && actor
            ? actor.fullName
            : notification.title

    return (
        <div
            className={cn(
                'relative w-[400px] bg-white rounded-xl shadow-2xl border border-gray-100 px-4 py-3 flex gap-3',
            )}
        >
            {/* Icon/Avatar */}
            {renderIcon()}

            {/* Content */}
            <div className="flex-1 min-w-0 space-y-1">
                <div className="flex justify-between items-start gap-2">
                    <h4 className="font-semibold text-sm text-foreground leading-tight line-clamp-1">{displayTitle}</h4>
                    <Button
                        onClick={handleClose}
                        variant="ghost"
                        size="iconSm"
                        className="h-5 w-5 p-0 shrink-0 -mt-0.5 -mr-1"
                    >
                        <X className="size-4" />
                    </Button>
                </div>

                <p className="text-xs leading-snug text-neutral-500 line-clamp-2">
                    <NotificationContent notification={notification} highlightApprovalStatus />
                </p>
            </div>
        </div>
    )
}
